import styled, { css, keyframes } from 'styled-components/macro';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClefVerteIcon,
  EcoLeaderIcon,
  GreenEarth,
  GreenGlobeIcon,
  Planet21Icon,
  RightArrowIcon,
  BoldArrowIcon,
  BadIcon,
  CommittedDestinationIcon,
  SoftMobilityIcon,
} from '@net0-calc/common/assets/svg';
import { copyResultsLink } from 'utils/copy-results-link';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { RedAccentHeading } from 'components';
import { HOTEL_ICON } from 'common/constants/picture-paths';
type Props = {
  answerId: string | null;
};

export const TipsToReduce: FC<Props> = ({ answerId }) => {
  const { t, i18n } = useTranslation();
  const resolvedLanguage = i18n.resolvedLanguage || 'en';

  return (
    <Root>
      <RedAccentHeading>
        <Title>
          <EmojiContainer>💡</EmojiContainer> {t('events.pages.result.tipsToReduce.title')}
        </Title>
      </RedAccentHeading>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.involveAttendees.title')}</span>
      </StepTitleWrap>

      <StepSubtitle>
        <span
          dangerouslySetInnerHTML={{
            __html: t('events.pages.result.tipsToReduce.tips.involveAttendees.text1'),
          }}></span>{' '}
        <ButtonInTextWrapper>
          <ButtonInText
            onClick={() => {
              window.open('https://visit.netzero.mc/', '_blank');
            }}>
            <span>{t('events.pages.result.tipsToReduce.visitCalculator')}</span>
            <BoldArrowIcon />
          </ButtonInText>
        </ButtonInTextWrapper>
        <br />
        <br />
        <span
          dangerouslySetInnerHTML={{
            __html: t('events.pages.result.tipsToReduce.tips.involveAttendees.text2'),
          }}></span>{' '}
        {answerId && (
          <ButtonInTextWrapper>
            <ButtonInText
              onClick={() => {
                copyResultsLink(answerId);
              }}>
              <span>{t('events.pages.result.tipsToReduce.getResults')}</span>
              <BoldArrowIcon />
            </ButtonInText>
          </ButtonInTextWrapper>
        )}
      </StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.setOptimalTemperature.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>{t('events.pages.result.tipsToReduce.tips.setOptimalTemperature.text')}</StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.manageWasteAndRecycle.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>
        {t('events.pages.result.tipsToReduce.tips.manageWasteAndRecycle.text')}{' '}
        <ButtonInTextWrapper>
          <ButtonInText
            onClick={() => {
              window.open('https://smaservices-monaco.com/fr/evenements', '_blank');
            }}>
            <span> {t('events.pages.result.tipsToReduce.tips.manageWasteAndRecycle.linkText')}</span>
            <BoldArrowIcon />
          </ButtonInText>
        </ButtonInTextWrapper>
      </StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.minimisePrising.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>{t('events.pages.result.tipsToReduce.tips.minimisePrising.text')}</StepSubtitle>

      <StepTitleWrap>
        <BadIcon />
        <span>{t('events.pages.result.tipsToReduce.tips.workWithLocalSuppliers.title')}</span>
      </StepTitleWrap>
      <StepSubtitle>{t('events.pages.result.tipsToReduce.tips.workWithLocalSuppliers.text')}</StepSubtitle>

      <LinksWrapper>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/26450/a-destination-with-values`}
          target="_blank">
          <StyledCommittedDestinationIcon />
          <LinkText>{t('events.pages.result.tipsToReduce.links.guide')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/37823/responsible-tourism-white-paper`}
          target="_blank">
          <StyledGreenEarth fill="var(--color2)" />
          <LinkText>{t('events.pages.result.tipsToReduce.links.whitepapert')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
        <Link
          href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/38669/soft-mobility-in-monaco`}
          target="_blank">
          <StyledSoftMobilityIcon />
          <LinkText>{t('events.pages.result.tipsToReduce.links.hotels')}</LinkText>
          <StyledRightArrowIcon />
        </Link>
      </LinksWrapper>
      <HotelsWrapper>
        <HotelsHeader>
          <HotelIcon src={HOTEL_ICON} />
          <HotelsTitle>{t('personal.pages.result.tipsToReduce.hotelsTitle')}</HotelsTitle>
        </HotelsHeader>
        <Paragraph>
          {t('events.pages.result.tipsToReduce.hotelParagraph')}
          <ParagraphLink
            target="_blank"
            href={`https://www.visitmonaco.com/${resolvedLanguage}/environment/26532/a-hotel-industry-committed-to-responsible-tourism`}>
            {' ' + t('events.pages.result.tipsToReduce.hotelParagraphLink')}
          </ParagraphLink>
        </Paragraph>
        <HotelsGrid>
          <HotelsContainer>
            <HotelName>Hotel de Paris</HotelName>
            <IconsWrapper>
              <GreenGlobeIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Hotel Métropole Monte-Carlo</HotelName>
            <IconsWrapper>
              <ClefVerteIcon />
              <EcoLeaderIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Hotel Hermitage Monte-Carlo</HotelName>
            <IconsWrapper>
              <GreenGlobeIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Hotel Monte-Carlo Beach</HotelName>
            <IconsWrapper>
              <GreenGlobeIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Hotel Fairmont Monte Carlo</HotelName>
            <IconsWrapper>
              <Planet21Icon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Méridien Beach Plaza</HotelName>
            <IconsWrapper>
              <ClefVerteIcon />
              <EcoLeaderIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Monte-Carlo Bay Hotel & Resort</HotelName>
            <IconsWrapper>
              <GreenGlobeIcon />
              <EcoLeaderIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Hotel Columbus</HotelName>
            <IconsWrapper>
              <EcoLeaderIcon />
            </IconsWrapper>
          </HotelsContainer>
          <HotelsContainer>
            <HotelName>Hotel Novotel Monte-Carlo</HotelName>
            <IconsWrapper>
              <Planet21Icon />
            </IconsWrapper>
          </HotelsContainer>
        </HotelsGrid>
      </HotelsWrapper>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 845px;
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    margin-bottom: 60px;
  }
`;

const Title = styled.div`
  ${TYPOGRAPHY.bodyMedium20}
  color: var(--color6);
  margin-bottom: 20px;
  display: flex;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold32}
    margin-bottom: 32px;
  }
`;

const Paragraph = styled.div`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color9);
  width: 100%;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const LinksWrapper = styled.div`
  max-width: 1117px;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    flex-direction: row;
    margin-bottom: 60px;
  }
`;

const HotelsWrapper = styled.div`
  margin-top: 30px;
  background: var(--color5);
  border: 1px solid var(--color10);
  border-radius: 16px;
  padding: 24px;

  @media ${DEVICE.laptop} {
    margin-top: 50px;
    padding: 32px;
    border-radius: 20px;
  }
`;

const HotelsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media ${DEVICE.laptop} {
    gap: 14px;
    margin-bottom: 20px;
  }
`;

const HotelsTitle = styled.h3`
  ${TYPOGRAPHY.bodySemibold18}
  color: var(--color6);
  margin: 0;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold24}
  }
`;

const HotelsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media ${DEVICE.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${DEVICE.laptop} {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
`;

const HotelsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--color14);
  transition: all 0.2s ease;

  &:hover {
    background: var(--color4);
  }
`;

const HotelName = styled.span`
  ${TYPOGRAPHY.bodyRegular14}
  color: var(--color6);
  flex: 1;
  min-width: 0;
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  & svg {
    height: 26px;
    width: auto;
  }
`;

const Link = styled.a`
  ${TYPOGRAPHY.bodyMedium16}
  padding: 14px 16px;
  display: flex;
  align-items: center;
  border-radius: 12px;
  border: 1px solid var(--color10);
  background: var(--color5);
  color: var(--color6);
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  width: 343px;

  &:not(:last-child) {
    margin: 0 0 12px 0;
  }

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium16}
    width: fit-content;
    padding: 20px;

    &:not(:last-child) {
      margin: 0 16px 0 0;
    }
  }

  &:hover {
    border-color: var(--color2);
    box-shadow: 0 4px 16px rgba(206, 17, 38, 0.1);
    transform: translateY(-2px);
    color: var(--color2);
  }
`;

const LinkText = styled.p`
  flex: 1;
  width: 100%;

  @media ${DEVICE.laptop} {
    white-space: nowrap;
  }
`;

const iconsCommonCSS = css`
  margin-right: 16px;
`;

const StyledCommittedDestinationIcon = styled(CommittedDestinationIcon)`
  ${iconsCommonCSS}
`;

const StyledSoftMobilityIcon = styled(SoftMobilityIcon)`
  ${iconsCommonCSS}
`;

const StyledGreenEarth = styled(GreenEarth)`
  ${iconsCommonCSS}
`;

const StyledRightArrowIcon = styled(RightArrowIcon)`
  margin-left: 16px;
  stroke: var(--color2);
`;

const emojiPulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const EmojiContainer = styled.div`
  ${TYPOGRAPHY.bodyMedium20}
  cursor: pointer;
  margin-right: 24px;
  &:hover {
    animation: ${emojiPulseAnimation} 0.2s ease-in normal;
  }

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold32}
    margin-right: 16px;
  }
`;

const StepTitleWrap = styled.div`
  $${TYPOGRAPHY.boydRegular20}
  color: var(--color6);
  display: flex;
  align-items: center;
  & svg {
    display: block;
    margin-right: 12px;
    flex-shrink: 0;
    width: 26px;
  }

  &:not(:last-child) {
    margin-bottom: 13px;
  }
`;
const StepSubtitle = styled.span`
  ${TYPOGRAPHY.bodyRegular16}
  position: relative;
  display: inline-block;
  padding-left: 38px;
  margin-bottom: 16px;
  color: var(--color9);
  &:nth-last-of-type(1) {
    margin-bottom: 20px;
  }
  @media ${DEVICE.mobileL} {
    margin-bottom: 30px;
    &:nth-last-of-type(1) {
      margin-bottom: 32px;
    }
  }
`;
const ButtonInTextWrapper = styled.span`
  display: inline-block;
`;

const ButtonInText = styled.div`
  ${TYPOGRAPHY.captionMedium12}
  width: auto;
  height: 26px;
  padding: 4px 8px 4px 8px;
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  border-radius: 8px;
  color: var(--color2);
  border: 2px solid rgba(197, 235, 197, 1);
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    border: 2px solid var(--color2);
  }
`;

const ParagraphLink = styled.a`
  text-decoration: none;
  color: var(--color2);
  font-weight: 500;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const HotelIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;

  @media ${DEVICE.laptop} {
    width: 32px;
    height: 32px;
  }
`;
